import { useState, useEffect } from "react";
import theme from "../data/theme";

// Interim About page — reuses the "Buy me a coffee" support text; a fuller
// mission version comes later. Pure presentational: no engine/pricing/seed
// touch. Reached via the /about route (wired in AuctionDraftBuilder) and the
// landing footer link.
export default function AboutPage({ onHome }) {
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const linkStyle = (id, base = theme.colors.text.faint, hover = theme.colors.text.secondary) => ({
    fontSize: 12,
    color: hovering === id ? hover : base,
    textDecoration: "none",
    letterSpacing: "0.5px",
    transition: "color 0.2s ease",
    cursor: "pointer",
  });

  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      fontFamily: theme.fonts.display,
      overflow: "hidden",
      position: "relative",
    }}>
      {/* Ambient glow */}
      <div style={{
        position: "absolute",
        top: "-20%",
        left: "50%",
        transform: "translateX(-50%)",
        width: "600px",
        height: "400px",
        background: `radial-gradient(ellipse, ${theme.helpers.brandBg()} 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
        textAlign: "center",
        maxWidth: 560,
        width: "100%",
        position: "relative",
        zIndex: 1,
      }}>
        {/* Logo — click returns home */}
        <div
          onClick={() => onHome && onHome()}
          style={{ cursor: "pointer", display: "inline-block" }}
          onMouseEnter={() => setHovering("logo")}
          onMouseLeave={() => setHovering(null)}
        >
          <svg viewBox="0 0 300 78" style={{ width: 220, height: 57, display: "block", margin: "0 auto 8px" }} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="ab-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={theme.colors.brand.orange} />
                <stop offset="100%" stopColor={theme.colors.brand.accent} />
              </linearGradient>
            </defs>
            <g transform="translate(61,0)">
              <text y="38" fontFamily="'Outfit', sans-serif" fontWeight="900" fontSize="44" letterSpacing="-1">
                <tspan fill="#ffffff">MOCK</tspan><tspan fill="url(#ab-grad)"> 1</tspan>
              </text>
              <text x="2" y="55" fontFamily="'Outfit', sans-serif" fontWeight="600" fontSize="12" fill="#ffffff" letterSpacing="4.5" opacity="0.9">AUCTION SUITE</text>
            </g>
          </svg>
        </div>

        {/* H1 */}
        <h1 style={{
          fontSize: isMobile ? 30 : 38,
          color: theme.colors.text.primary,
          margin: "20px 0 10px",
          fontWeight: 800,
          fontFamily: theme.fonts.display,
          letterSpacing: "0.5px",
        }}>
          About
        </h1>

        {/* Sub-heading */}
        <p style={{
          fontSize: isMobile ? 14 : 15,
          color: theme.colors.brand.accent,
          fontWeight: 700,
          margin: "0 0 28px",
          letterSpacing: "0.3px",
          lineHeight: 1.4,
        }}>
          The only fantasy football auction suite powered by real auction data
        </p>

        {/* Body */}
        <p style={{
          fontSize: 15,
          color: theme.colors.text.secondary,
          lineHeight: 1.7,
          margin: "0 0 32px",
          fontFamily: theme.fonts.body,
          textAlign: "left",
        }}>
          I'm just a guy that loves building fantasy football tools, analyzing data, and laughing, cooking and eating my way around the world one year at a time. This started as a nights-and-weekends hobby and I'd love to keep growing it. Every coffee goes straight back into building and expanding these tools. (Turns out cool stuff is expensive.) I hope you all enjoy using and playing with these tools as much as I enjoyed building them.
        </p>

        {/* Buy me a coffee */}
        <a
          href="https://buymeacoffee.com/mock1"
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => setHovering("bmac")}
          onMouseLeave={() => setHovering(null)}
          style={{
            display: "inline-block",
            background: hovering === "bmac"
              ? "linear-gradient(135deg, #FF8C00, #FF6B00)"
              : theme.colors.brand.gradientBtn,
            color: "#fff",
            textDecoration: "none",
            padding: "14px 36px",
            fontSize: 15,
            fontWeight: 800,
            fontFamily: theme.fonts.display,
            letterSpacing: "1px",
            borderRadius: 12,
            boxShadow: hovering === "bmac"
              ? "0 8px 32px rgba(255,107,0,0.4)"
              : "0 4px 20px rgba(255,107,0,0.25)",
            transform: hovering === "bmac" ? "scale(1.03)" : "scale(1)",
            transition: "all 0.2s ease",
          }}
        >
          ☕ Buy me a coffee
        </a>

        {/* Contact */}
        <p style={{
          fontSize: 13,
          color: theme.colors.text.faint,
          margin: "28px 0 0",
          fontFamily: theme.fonts.body,
        }}>
          Bug? Idea?{" "}
          <a
            href="mailto:mock1app@gmail.com"
            style={{ color: theme.colors.brand.accent, textDecoration: "none" }}
          >
            mock1app@gmail.com
          </a>
        </p>

        {/* Footer nav */}
        <div style={{
          marginTop: 48,
          display: "flex",
          justifyContent: "center",
          gap: 24,
          alignItems: "center",
        }}>
          <span
            onClick={() => onHome && onHome()}
            onMouseEnter={() => setHovering("home")}
            onMouseLeave={() => setHovering(null)}
            style={linkStyle("home")}
          >
            ← Home
          </span>
          <span style={{ fontSize: 11, color: theme.colors.border.default, letterSpacing: "1px" }}>
            &copy; 2026 MOCK 1
          </span>
        </div>
      </div>
    </div>
  );
}
